'use client';

import { Calendar, AlertTriangle, TrendingUp } from 'lucide-react';
import { CVEData } from '../utils/nvdApi';
import { KEVEntry } from '../utils/cisaKev';
import { formatEPSSScore, formatEPSSPercentile, getEPSSSeverity } from '../utils/epssApi';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs';
import KEVBadge from '../components/KEVBadge';

interface CVEHeroProps {
  cve: CVEData;
  kevEntry?: KEVEntry | null;
  breadcrumbs?: BreadcrumbItem[];
}

// Check if a CWE ID is valid and linkable (not a placeholder like NVD-CWE-noinfo)
function isValidCWEId(cweId: string): boolean {
  return /^CWE-\d+$/i.test(cweId);
}

function getHeroGradient(severity: string | undefined): string {
  switch (severity?.toUpperCase()) {
    case 'CRITICAL':
      return 'from-red-700 to-red-600';
    case 'HIGH':
      return 'from-orange-700 to-orange-600';
    case 'MEDIUM':
      return 'from-amber-700 to-yellow-700';
    case 'LOW':
      return 'from-blue-700 to-blue-600';
    default:
      return 'from-gray-700 to-gray-600';
  }
}

export default function CVEHero({ cve, kevEntry, breadcrumbs }: CVEHeroProps) {
  const gradient = getHeroGradient(cve.cvssV3?.baseSeverity);
  const score = cve.cvssV3?.baseScore ?? cve.cvssV2?.baseScore;
  const severity = cve.cvssV3?.baseSeverity;

  return (
    <section className={`bg-gradient-to-br ${gradient} text-white py-8 md:py-12 px-4 md:px-6 rounded-xl shadow-lg mb-6`}>
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="mb-4 overflow-x-auto pb-2 -mx-2 px-2">
            <Breadcrumbs
              items={breadcrumbs}
              className="text-white [&_a]:text-white [&_a:hover]:text-white [&_a]:underline"
            />
          </div>
        )}

        {/* CVE ID */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 break-all">
          {cve.id}
        </h1>

        {/* Severity Scores - CVSS and EPSS side by side */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-6 mb-6">
          {/* CVSS Score */}
          {score !== undefined && (
            <div className="flex items-center gap-4">
              <span className="text-5xl md:text-6xl font-bold tabular-nums">
                {score.toFixed(1)}
              </span>
              <div className="flex flex-col">
                {severity && (
                  <span className="text-xl md:text-2xl font-semibold">
                    {severity}
                  </span>
                )}
                <span className="text-white/90 text-sm">
                  CVSS v{cve.cvssV3 ? '3.1' : '2.0'} Base Score
                </span>
              </div>
            </div>
          )}

          {!score && (
            <div className="flex items-center gap-3 text-white">
              <AlertTriangle className="w-6 h-6" />
              <span className="text-lg">CVSS Score Not Available</span>
            </div>
          )}

          {/* EPSS Score - Exploitation Probability */}
          {cve.epss && (
            <div className="flex items-center gap-4 sm:border-l sm:border-white/30 sm:pl-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-8 h-8 text-white" />
                <span className="text-4xl md:text-5xl font-bold tabular-nums">
                  {formatEPSSScore(cve.epss.score)}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg md:text-xl font-semibold">
                  {getEPSSSeverity(cve.epss.score)} Risk
                </span>
                <span className="text-white text-sm">
                  EPSS ({formatEPSSPercentile(cve.epss.percentile)})
                </span>
              </div>
            </div>
          )}
        </div>

        {/* KEV Badge if applicable */}
        {kevEntry && (
          <div className="mb-6">
            <KEVBadge kevEntry={kevEntry} variant="inline" className="bg-white dark:bg-gray-800 text-red-600 dark:text-red-400" />
          </div>
        )}

        {/* CWE Badges */}
        {cve.cwe && cve.cwe.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {cve.cwe.map((cweId) => (
              isValidCWEId(cweId) ? (
                <a
                  key={cweId}
                  href={`/tools/security/cwe-lookup/${cweId.toLowerCase()}`}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-white/30 dark:bg-gray-800/30 text-white hover:bg-white dark:hover:bg-gray-800/40 transition-colors"
                >
                  {cweId}
                </a>
              ) : (
                <span
                  key={cweId}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-white/30 dark:bg-gray-800/30 text-white"
                  title="Placeholder CWE - no specific weakness assigned"
                >
                  {cweId}
                </span>
              )
            ))}
          </div>
        )}

        {/* Quick description */}
        <p className="text-base md:text-lg text-white leading-relaxed line-clamp-3 max-w-4xl">
          {cve.description}
        </p>

        {/* Dates */}
        <div className="flex flex-wrap gap-4 mt-4 text-sm text-white">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>Published: {new Date(cve.published).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>Modified: {new Date(cve.lastModified).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
